/**
 * GLSL code and material creation for stroked wide lines.
 * @module
 */
import stableHash from "common/utils/stableHash";
import * as THREE from "three";
import { defaultStencilProperties } from "../defaultStencilProperties";
import type { HighlightDataTexture } from "../HighlightDataTexture";
import { includeEntityHighlight } from "./customizeShader";

export class LineBasicMaterialFactory {
  private materials: { [key: string]: THREE.LineBasicMaterial } = {};
  private dashedMaterials: { [key: string]: THREE.LineDashedMaterial } = {};

  private static _instance: LineBasicMaterialFactory | undefined;

  static get instance(): LineBasicMaterialFactory {
    return this._instance ?? (this._instance = new LineBasicMaterialFactory());
  }

  private highlightDataTextureUniform: THREE.IUniform = { value: null };
  private isolateModeUniform: THREE.IUniform = { value: null };

  private constructor() {}

  getMaterial(options: Partial<THREE.LineBasicMaterialParameters>): THREE.LineBasicMaterial {
    const usedOptions: Partial<THREE.LineBasicMaterialParameters> = {
      ...defaultStencilProperties,
      ...options,
      transparent: true, // The transparent flag affects render order, so we always set 2D objects as transparent.
      name: "LineBasicMaterialFactory Material",
    };
    const hash = stableHash(usedOptions); // TODO normalize color
    if (this.materials[hash]) {
      return this.materials[hash];
    }

    const material = (this.materials[hash] = new THREE.LineBasicMaterial(usedOptions));
    material.defines = { ISOLATE_MODE: 1 };

    material.onBeforeCompile = (shader) => {
      const { uniforms } = shader;

      uniforms.highlightedIds = this.highlightDataTextureUniform;
      uniforms.isolateMode = this.isolateModeUniform;

      return includeEntityHighlight(shader, true);
    };

    return material;
  }

  getDashedMaterial(options: Partial<THREE.LineDashedMaterialParameters>): THREE.LineDashedMaterial {
    const usedOptions: Partial<THREE.LineDashedMaterialParameters> = {
      ...defaultStencilProperties,
      ...options,
      transparent: true, // The transparent flag affects render order, so we always set 2D objects as transparent.
      name: "LineBasicMaterialFactor Dashed Material",
    };
    const hash = stableHash(usedOptions); // TODO normalize color
    if (this.dashedMaterials[hash]) {
      return this.dashedMaterials[hash];
    }

    const material = (this.dashedMaterials[hash] = new THREE.LineDashedMaterial(usedOptions));

    material.onBeforeCompile = (shader) => {
      const { uniforms } = shader;

      uniforms.highlightedIds = this.highlightDataTextureUniform;
      uniforms.isolateMode = this.isolateModeUniform;

      return includeEntityHighlight(shader, true);
    };

    return material;
  }

  prepareRender(highlightDataTexture?: HighlightDataTexture) {
    this.highlightDataTextureUniform.value = highlightDataTexture?.texture ?? null;
    this.isolateModeUniform.value = highlightDataTexture?.isolateMode ?? false;
  }
}
